// Truck cabinet touchscreen ("kiosk console") support.
//
// The console runs Chromium on the truck's Raspberry Pi, showing this same app. It has no
// credentials of its own: it asks the truck-proxy on that Pi (over http://localhost:5005) who is
// signed in, and displays a QR code until a driver approves it from the phone they're already
// signed in on.
//
// Two things make this work without touching the rest of the app:
//   - the kiosk token carries the same claims as an Azure AD B2C driver token, so accountReducer
//     decodes it exactly as it would a normal sign-in;
//   - the session lives on the Pi's /data volume rather than in browser storage, so a reboot or a
//     wiped Chromium profile doesn't force the driver to pair again.

import * as jwtdecode from 'jwt-decode/lib';

const KIOSK_MODE_KEY = 'kioskMode';
const KIOSK_LOCAL_BASE_KEY = 'kioskLocalBase';

// The truck-proxy on this device. Port 5005 because Kestrel is configured to listen on 5005/5006
// whenever a certificate is present, which is the case both on the bench and on the truck (the
// container's start.sh writes cert.pem from the SSL_CERT fleet var) - so the app is never on port
// 80 in practice. Overridable from the console (localStorage) for a truck-proxy on another port.
export const KIOSK_DEFAULT_LOCAL_BASE = 'http://localhost:5005';

export const defaultLocalBase = () => {
    try {
        return localStorage.getItem(KIOSK_LOCAL_BASE_KEY) || KIOSK_DEFAULT_LOCAL_BASE;
    } catch (e) {
        return KIOSK_DEFAULT_LOCAL_BASE;
    }
};

/**
 * Whether this browser is a truck console. Sticky in localStorage because the app reloads itself
 * (Main's idle timer, AutoReload) and must come back signed in rather than bouncing to B2C.
 */
export function isKioskMode() {
    try {
        return localStorage.getItem(KIOSK_MODE_KEY) === '1';
    } catch (e) {
        return false;
    }
}

export function setKioskMode(on) {
    try {
        if (on) {
            localStorage.setItem(KIOSK_MODE_KEY, '1');
        } else {
            localStorage.removeItem(KIOSK_MODE_KEY);
        }
    } catch (e) {
        // Private mode or storage disabled — kiosk mode just won't survive a reload.
    }
}

/**
 * Asks the truck-proxy on this device for the console's session.
 *
 * Resolves to { paired: true, token, driverName, truckNumberPlate, expiresAt } when a driver is
 * signed in, { paired: false, userCode, ... } when a code should be displayed, or
 * { unreachable: true } when the device can't be reached at all — which on a truck usually means the
 * kiosk container is talking to a truck-proxy that isn't up yet.
 */
export function fetchKioskSession() {
    return fetch(`${defaultLocalBase()}/kiosk/session`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
    })
        .then(response => response.json().then(body => ({ ...body, status: response.status })))
        .catch(() => ({ unreachable: true }));
}

/** Driver-initiated sign-out at the console. Clears the token held on the Pi. */
export function clearKioskSession() {
    return fetch(`${defaultLocalBase()}/kiosk/session`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
    }).catch(() => null);
}

/**
 * Puts a kiosk token into the store the same way a B2C sign-in would.
 *
 * redux-oidc's own reducer stores this payload as state.oidc.user (which request.js and truckHub.js
 * read for the bearer token), and accountReducer JWT-decodes access_token for extension_userId,
 * extension_userRole, signInNames.emailAddress and name. Since the kiosk token carries exactly those
 * claims, everything downstream behaves as though the driver signed in normally.
 */
export function kioskUserFoundAction(session) {
    const expiresAt = session.expiresAt ? Math.floor(new Date(session.expiresAt).getTime() / 1000) : undefined;

    return {
        type: 'redux-oidc/USER_FOUND',
        payload: {
            access_token: session.token,
            // The app only ever decodes access_token; id_token is carried for shape compatibility.
            id_token: session.token,
            token_type: 'Bearer',
            expires_at: expiresAt,
            profile: {},
        },
    };
}

/**
 * The truck a kiosk token is bound to, read from its kiosk_plate claim.
 *
 * Taken from the token rather than held separately so it can't drift from the session the API
 * actually issued — the same reason the plate isn't baked into the console's launch URL.
 */
export function getKioskPlate(accessToken) {
    if (!accessToken) {
        return null;
    }

    try {
        return jwtdecode.default(accessToken).kiosk_plate || null;
    } catch (e) {
        return null;
    }
}
